/**
 * RichTextEditor — the rich-text box, with the one fix every caller needs.
 *
 * `react-quill` 2.0.0 was written for Quill 1, where
 * `clipboard.convert("<p>…</p>")` took an HTML string. package.json overrides
 * Quill to 2.x, where the same call takes `{ html }`; handed a bare string it
 * finds no `html` and returns an EMPTY document. react-quill makes that call
 * whenever its `value` changes from outside (the first render, a template
 * being picked, a saved draft being reopened), so the box stayed on its
 * placeholder while the form's state, and the email that would be sent, held
 * the full text. Typing was unaffected, which is why it went unnoticed.
 *
 * Importing this module corrects that one step on react-quill's prototype
 * (once), then re-exports the editor unchanged. Use it instead of importing
 * `react-quill` directly; props and ref methods are the same.
 */

import ReactQuill from "react-quill";

type Clipboard = { convert: (input: unknown) => unknown; convertHTML?: unknown };
type EditorLike = { clipboard: Clipboard; setContents: (delta: unknown) => void };

const PATCHED = Symbol.for("clubcentral.reactQuillQuill2Patch");

export function patchReactQuillForQuill2(target: any): void {
  const proto = target && target.prototype;
  // Test files replace react-quill with a plain function component; nothing to patch.
  if (!proto || typeof proto.setEditorContents !== "function" || proto[PATCHED]) return;
  const original = proto.setEditorContents;
  proto.setEditorContents = function setEditorContents(editor: EditorLike, value: unknown) {
    const clipboard = editor && editor.clipboard;
    // Quill 1 (no convertHTML) keeps react-quill's own behavior.
    if (typeof value !== "string" || !clipboard || typeof clipboard.convertHTML !== "function") {
      return original.call(this, editor, value);
    }
    this.value = value;
    const selection = this.getEditorSelection();
    editor.setContents(clipboard.convert({ html: value }));
    // Same as react-quill: restore the cursor once the content has settled.
    setTimeout(() => this.setEditorSelection(editor, selection));
  };
  proto[PATCHED] = true;
}

patchReactQuillForQuill2(ReactQuill);

export default ReactQuill;
